import React, { useCallback } from 'react';

import type { DocNode } from '@atlaskit/adf-schema';
import type { PublicPluginAPI } from '@atlaskit/editor-common/types';
import type { TrackChangesPlugin } from '@atlaskit/editor-plugin-track-changes';

import { ObjectSidebarControl } from '@confluence/object-sidebar-components/entry-points/ObjectSidebarControl.tsx';

import { useSharedAIState } from './hooks/useSharedAIState.ts';

interface ObjectSidebarControlFullPageEditorProps {
	editorApi: PublicPluginAPI<[TrackChangesPlugin]> | undefined;
	hasAiButton: boolean;
	hasAudioButton: boolean;
	contentId: string;
	spaceKey: string;
	contentType: string;
	contentTitle: string;
	isUnpublishedDraft?: boolean;
	disabled?: boolean;
	isEditorEmpty?: boolean;
}

export function ObjectSidebarControlFullPageEditor({
	editorApi,
	hasAiButton,
	hasAudioButton,
	contentId,
	spaceKey,
	contentType,
	contentTitle,
	isUnpublishedDraft,
	disabled,
	isEditorEmpty,
}: ObjectSidebarControlFullPageEditorProps): React.JSX.Element {
	const { aiIsStreaming } = useSharedAIState(editorApi);

	const getAdfDocument = useCallback((): Promise<DocNode | null> => {
		return new Promise((resolve) => {
			if (editorApi?.core?.actions?.requestDocument) {
				editorApi.core.actions.requestDocument((doc) => {
					resolve(doc as DocNode);
				});
			} else {
				resolve(null);
			}
		});
	}, [editorApi]);

	return (
		<ObjectSidebarControl
			key="object-sidebar-control"
			hasCommentsButton
			hasDetailsButton
			hasLoomButton
			hasAiButton={hasAiButton}
			hasRemixButton
			hasAudioButton={hasAudioButton}
			aiIsStreaming={aiIsStreaming}
			contentId={contentId}
			spaceKey={spaceKey}
			contentType={contentType}
			contentTitle={contentTitle}
			isUnpublishedDraft={isUnpublishedDraft}
			disabled={disabled}
			isEditorEmpty={isEditorEmpty}
			getAdfDocument={getAdfDocument}
		/>
	);
}
